import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import useAuth from '../../../../../hooks/useAuth';
import useAxiosProtect from '../../../../../hooks/useAxiosProtect';
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const CSVDownloadModal = props => {
    const { auth, persist } = useAuth();
    const axiosProtect = useAxiosProtect();
    const [downloadStatus, setDownloadStatus] = useState({ loading: false, error: '', message: '', progress: 0 });
    const [siteName, setSiteName] = useState('');

    const siteID = props.siteID === undefined
        ? window.location.href.split('/')[window.location.href.split('/').length - 1]
        : props.siteID;

    const [startDate, setStartDate] = useState(props.selectedDates.start);
    const [endDate, setEndDate] = useState(props.selectedDates.end);
    const [dataType, setDataType] = useState(props.plotSpecs.loggedUnits ?? "adjusted");

    const [resolution, setResolution] = useState(props.plotSpecs.resolution);
    const [activeUtility, setActiveUtility] = useState(props.plotSpecs.activeUtility);
    const [metricType, setMetricType] = useState(props.plotSpecs.metricType);
    const [metricOptions, setMetricOptions] = useState(props.plotSpecs.metricOptions);
    const [aggregateMethod, setAggregateMethod] = useState(props.plotSpecs.aggregateMethod);

    const processAggregateMethod = metric => {
        if([
            'L/min',
            'Current (A)',
            'kW',
            'm³/h',
            'impgal/min',
            'USG/min',
            '°C',
            '°F', 'lbs/h', 'kg/h', 'BTU/h', 'kJ/h'
        ].includes(metric)) return 'average';

        return 'sum';
    }
    
    const generateMetricOptions = utility => {
        if (utility === 'water') {
            return ['L/min', 'L', 'm³', 'm³/h'];
        } else if (utility === 'electricity') {
            return ['Current (A)', 'kW', 'kWh'];
        } else if (utility === 'gas') {
            return ['m³/h', 'm³'];
        } else if (utility === 'steam') {
            return ['lbs/h', 'kg/h', 'BTU/h', 'kJ/h'];
        } else if (utility === 'temperature') {
            return ['°C', '°F'];
        }
        return [];
    };    

    useEffect(() => {
        if (props.csvModalOpen) {
            setStartDate(props.selectedDates.start);
            setEndDate(props.selectedDates.end);
        }
    }, [props.csvModalOpen]);

    useEffect(() => {
        setAggregateMethod(processAggregateMethod(metricType));
    }, [metricType]);

    useEffect(() => {
        if (!props.csvModalOpen || !siteID) return;

        axiosProtect.get(`/s/sites/get-site-fields?fields=%5B%22name%22%5D&siteID=${siteID}`)
            .then(response => {
                if (response.status === 200 && response.data?.siteData?.name) {
                    setSiteName(response.data.siteData.name);
                }
            })
            .catch(err => console.log(err));
    }, [props.csvModalOpen, siteID]);

    const requestCSVDownload = async () => {
        if(!startDate || !endDate){
            setDownloadStatus({
                loading: false,
                error: 'Please select a start and end date.'
            });
            return;
        }

        let waterUnits =
            activeUtility !== 'water'
                ? undefined
                : metricType === 'm³/h'
                    ? 'm3h'
                    : metricType === 'm³'
                        ? 'm3'
                        : metricType;

        let tempUnits =
            activeUtility !== 'temperature'
                ? undefined
                : metricType === '°C'
                    ? 'C'
                    : 'F';

        const payload = {
            site_id: parseInt(siteID),
            site_name: siteName,
            utility: activeUtility,
            unit: metricType,
            data_type: dataType,
            resolution: resolution,
            start_date: startDate.format('YYYY-MM-DD HH:mm:ss'),
            end_date: endDate.format('YYYY-MM-DD HH:mm:ss'),
            timezone: props.selectedDates.tzName,
            email: auth?.email,
            waterUnits,
            tempUnits,
            method: aggregateMethod
        };

        setDownloadStatus({
            loading: true,
            error: '',
            message: '',
            progress: 0
        });

        try {
            const rawDataRoute = props.useV2RawData ? 'raw-data-v2' : 'raw-data';
            const response = await axiosProtect.post(
                `r/${rawDataRoute}/download`,
                payload
            );

            const jobId = response.data?.jobId;

            if (!jobId) {
                throw new Error('No CSV job ID returned');
            }

            console.log('CSV job started:', jobId);

            const pollStatus = async () => {
                try {
                    const statusResponse = await axiosProtect.get(
                        `r/${rawDataRoute}/download/status`,
                        {
                            params: { jobId }
                        }
                    );

                    const job = statusResponse.data;

                    if (job.status === 'complete') {
                        console.log('CSV complete');

                        setDownloadStatus({
                            loading: false,
                            error: '',
                            message: job.message,
                            progress: job.progress
                        });

                        return;
                    }

                    if (job.status === 'error') {
                        console.error('CSV job failed:', job.error);

                        setDownloadStatus({
                            loading: false,
                            error:
                                job.error ??
                                'Failed to generate CSV — please try again.',
                            message: job.message ?? '',
                            progress: job.progress ?? 100
                        });

                        return;
                    }

                    setDownloadStatus({
                        loading: true,
                        error: '',
                        message: job.message,
                        progress: job.progress
                    });

                    setTimeout(pollStatus, 250);

                } catch (err) {
                    console.error('Failed to check CSV status:', err);

                    setDownloadStatus({
                        loading: false,
                        error:
                            err.response?.data?.error ??
                            'Failed to check CSV status — please try again.',
                        message: '',
                        progress: 0
                    });
                }
            };

            pollStatus();

        } catch (err) {
            console.error('Failed to start CSV request:', err);

            setDownloadStatus({
                loading: false,
                error:
                    err.response?.data?.error ??
                    'Failed to request CSV — please try again.',
                message: '',
                progress: 0
            });
        }
    };

    const closeModal = () => {
        props.setcsvModalOpen(false);
    }
    
    return (
        <Modal
            show={props.csvModalOpen}
            onHide={() => props.setcsvModalOpen(false)}
            backdrop="static"
            centered
            enforceFocus={false}
            restoreFocus={false}
            className="opp-modal-root"
            backdropClassName="opp-modal-backdrop"
            dialogClassName="opp-modal-cw"
        >
            <Modal.Header closeButton>
                <h4>CSV Download - {siteName}</h4>
            </Modal.Header>
            <Modal.Body style={{ overflow: "visible" }}>
                {
                    (downloadStatus.loading || downloadStatus.progress > 0) ?
                    <>
                    <div style={{display: 'flex', flexDirection: 'row'}}>
                        {
                            downloadStatus.loading ?
                            <MoreHorizIcon style={{color:'lightgrey'}}/> :
                            downloadStatus.error !== '' ?
                            <CloseIcon style={{color:'red'}}/> :
                            <CheckIcon style={{color:'green'}}/>
                        }
                        <p>{downloadStatus.message} ({downloadStatus.progress}%)</p>
                    </div>
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress
                            variant="buffer"
                            value={downloadStatus.progress}
                            valueBuffer={Math.min(downloadStatus.progress + 25, 100)}
                            aria-label="Loading…"
                        />
                    </Box>
                    </>
                    :
                    <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
                    <FormControl fullWidth>
                        <InputLabel id="utility-label">Utilities</InputLabel>

                            <Select
                                labelId="utility-label"
                                label="Utilities"
                                value={activeUtility}
                                MenuProps={{
                                    disablePortal: true,
                                    keepMounted: true,
                                    disableScrollLock: true,
                                }}
                                onChange={(e) => {
                                    const newUtility = e.target.value;
                                    const newOptions = generateMetricOptions(newUtility);
                                    
                                    setActiveUtility(newUtility);
                                    setMetricType(newOptions[0] ?? '');
                                    setMetricOptions(newOptions);
                                }}
                            >
                        <MenuItem value="water">Water</MenuItem>
                            <MenuItem value="electricity">Electricity</MenuItem>
                            <MenuItem value="gas">Gas</MenuItem>
                            <MenuItem value="steam">Steam</MenuItem>
                            <MenuItem value="temperature">Temperature</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="units-label">Units</InputLabel>

                        <Select
                            value={metricType}
                            label="Units"
                            MenuProps={{
                                disablePortal: true,
                                keepMounted: true,
                                disableScrollLock: true,
                            }}
                            onChange={(e) =>
                                setMetricType(e.target.value)
                            }
                        >
                            {metricOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>  
                    </div>
                    <div style={{ marginTop: "24px", marginBottom: "16px" }}>
                        <FormLabel sx={{ mb: 0.25 }}>
                            Data Type
                        </FormLabel>
                        <RadioGroup
                            row
                            value={dataType}
                            onChange={(e) => setDataType(e.target.value)}
                            sx={{ gap: 2 }}
                        >
                            <FormControlLabel value="adjusted" control={<Radio />} label="Adjusted" />
                            <FormControlLabel value="raw" control={<Radio />} label="Raw" />
                        </RadioGroup>
                    </div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
                            <DateTimePicker
                                label="Start Date and Time"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                ampm={false}
                                ampmInClock={false}
                                timezone={props.selectedDates.tzName}
                                slotProps={{
                                    textField: { fullWidth: true, inputProps: { readOnly: true } },
                                    popper: {
                                        disablePortal: true,
                                        modifiers: [
                                            { name: "eventListeners", options: { scroll: false, resize: true } },
                                            { name: "flip", enabled: false },
                                        ],
                                    },
                                }}
                                disableFuture
                                maxDateTime={endDate ? endDate.subtract(1, 'minute') : dayjs()}
                            />
                            <DateTimePicker
                                label="End Date and Time"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                ampm={false}
                                ampmInClock={false}
                                timezone={props.selectedDates.tzName}
                                slotProps={{
                                    textField: { fullWidth: true, inputProps: { readOnly: true } },
                                    popper: {
                                        disablePortal: true,
                                        modifiers: [
                                            { name: "preventOverflow", options: { boundary: "viewport" } },
                                            { name: "flip", enabled: false },
                                        ],
                                    },
                                }}
                                disableFuture
                                minDateTime={startDate ? startDate.add(1, 'minute') : undefined}
                                maxDateTime={dayjs()}
                            />
                        </div>
                    </LocalizationProvider>
                    <FormControl fullWidth style={{ marginTop: "16px" }}>
                        <InputLabel id="resolution-select-label">Resolution</InputLabel>
                        <Select
                            labelId="resolution-select-label"
                            id="resolution-select"
                            value={resolution}
                            label="Resolution"
                            MenuProps={{
                                disablePortal: true,
                                keepMounted: true,
                                disableScrollLock: true,
                            }}
                            onChange={(e) =>
                                setResolution(e.target.value)
                            }
                        >
                            {(() => {
                                if (!startDate || !endDate) return null;
                                const minutes = Math.abs(endDate.diff(startDate, 'minute', true));
                                const days = minutes / (60 * 24);
                                let options = ['1m', '5m', '15m'];
                                if (minutes >= 60) options.push('1h');
                                if (days >= 3) options.push('1d');
                                const labels = {
                                    '1m': '1 Minute',
                                    '5m': '5 Minutes',
                                    '15m': '15 Minutes',
                                    '1h': '1 Hour',
                                    '1d': '1 Day',
                                };
                                return options.map(res => (
                                    <MenuItem key={res} value={res}>{labels[res]}</MenuItem>
                                ));
                            })()}
                        </Select>
                    </FormControl>

                    {downloadStatus.error !== '' &&
                        <div style={{ width: '100%', textAlign: 'center', marginBottom: '1%' }}>
                            <small style={{ color: 'red' }}>{downloadStatus.error}</small>
                        </div>
                    }
                    </>
                }
            </Modal.Body>
            <Modal.Footer
                style={{
                    justifyContent: "flex-end",
                    paddingTop: "18px",
                }}
            >
                <Button
                    style={{ border: 'none', backgroundColor: 'rgb(8,114,182)' }}
                    disabled={downloadStatus.loading}
                    onClick={downloadStatus.progress === 100 ? closeModal : requestCSVDownload}
                >
                    {downloadStatus.loading ? 'Sending...' : downloadStatus.progress === 100 ? 'Close' : 'Download'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CSVDownloadModal;
